import React, { useState, useEffect } from "react";
import { Spinner } from "../../Common";
import {
  getInvestingPartners,
  getInvestingPartnerDetails,
  updateInvestingPartner,
} from "../../api";
import styles from "./partners.module.css";
import DataTable from "./../../Common/DataTable/DataTable";
import Button from "../../Common/Button";
import Offcanvas from "react-bootstrap/Offcanvas";
import CreatePartner from "./CreatePartner";
import BulkCreatePartner from "./BulkCreatePartner";
import singleBackArrow from "../../public/images/singleBackArrow.svg";
import DetailsTable from "./Detailstable";
import { InputBox } from "../../Common";
import StatusUpdate from "./StatusUpdate";
import { partnerColumns } from "../../utils/tableHeader";
import PartnerConfig from "./PartnerConfig";
import withFeatureActions from "../../withFeatureActions";
import { CookieUtils } from "../../axios.config";

const Partner = ({ featureAccess }) => {
  const { readAccess, writeAccess, modifyAccess } = featureAccess;
  const [partners, setPartners] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [canvasData, setCanvasData] = useState();
  const [showDetailsCanvas, setShowDetailsCanvas] = useState(false);
  const [stepToShow, setStepToShow] = useState(1);
  const [formData, setFormData] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [mode, setMode] = useState("create");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(20);
  const [searchText, setSearchText] = useState("");

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [newStatus, setNewStatus] = useState("active");
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [statusUpdateMessage, setStatusUpdateMessage] = useState("");
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);

  const [showConfigForm, setShowConfigForm] = useState(false);
  const [selectedPartnerCd, setSelectedPartnerCd] = useState("");

  const fetchPartnerData = async (
    pageNo = currentPage,
    filter = searchText
  ) => {
    setDataLoading(true);
    try {
      const response = await getInvestingPartners(pageNo, pageSize, filter);
      if (response.code === 200 && response.data) {
        setPartners(response?.data?.partners || []);
        const totalCount = response.data.total || 0;
        setTotalPages(Math.ceil(totalCount / pageSize));
      } else {
        setErrorMessage("Failed to fetch Partner data.");
      }
    } catch (error) {
      setErrorMessage("Error fetching data.");
    }
    setDataLoading(false);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleViewDetails = async (partnerCd) => {
    setDataLoading(true);
    try {
      const response = await getInvestingPartnerDetails(partnerCd);
      if (response.status === 200 && response.data) {
        setCanvasData(response?.data?.data);
        setShowDetailsCanvas(true);
      } else {
        setErrorMessage("Failed to fetch Partner details.");
      }
    } catch (error) {
      setErrorMessage("Error fetching Partner details.");
    }
    setDataLoading(false);
  };
  const handleAddConfig = (partner) => {
    setSelectedPartnerCd(partner.code);
    setShowConfigForm(false);
    setStepToShow(3);
  };
  const handleUpdatePartner = async (partnerCd) => {
    setDataLoading(true);
    try {
      const response = await getInvestingPartnerDetails(partnerCd);
      if (response.status === 200 && response.data) {
        const fetchedData = response?.data?.data;

        const transformedData = {
          partnerCd: partnerCd,
          name: fetchedData?.partnerBasic?.name || "",
          code: fetchedData?.partnerBasic?.code || "",
          host: fetchedData?.partnerBasic?.host || "",
          logo: fetchedData?.partnerBasic?.logo || "",
          location: fetchedData?.partnerBasic?.location || "",
          description: fetchedData?.partnerBasic?.description || "",
          status: fetchedData?.partnerBasic?.status?.toLowerCase() || "active",
          createdBy: fetchedData?.partnerBasic?.createdBy || "",
          type: Array.isArray(fetchedData?.partnerBasic?.type)
            ? fetchedData?.partnerBasic?.type
            : [fetchedData?.partnerBasic?.type || ""],
          scopes: fetchedData?.partnerBasic?.apiConfiguration?.scopes || [],
        };
        setFormData(transformedData);
        setShowDetailsCanvas(false);
        setStepToShow(2);
      } else {
        setErrorMessage("Failed to fetch Partner details.");
      }
    } catch (error) {
      setErrorMessage("Error fetching Partner details.");
    }
    setDataLoading(false);
  };
  const handleUpdateStatus = (partner) => {
    setSelectedPartner(partner);
    setNewStatus(partner.status?.toLowerCase() || "active");
    setShowStatusModal(true);
    setStatusUpdateMessage("");
  };

  const getCurrentUsername = () => {
    const cookieUserName = CookieUtils.get("username");
    if (cookieUserName) {
      try {
        return JSON.parse(cookieUserName);
      } catch {
        return cookieUserName;
      }
    }
    return "";
  };


  const submitStatusUpdate = async () => {
    if (!selectedPartner || !newStatus) return;

    setStatusUpdateLoading(true);
    try {
      const response = await updateInvestingPartner(selectedPartner.code, {
        status: newStatus,
        updatedBy: getCurrentUsername()
      });

      if (response.code === 200) {
        setStatusUpdateMessage(
          response.data?.message || "Status updated successfully"
        );
        setTimeout(() => {
          setShowStatusModal(false);
          setStatusUpdateMessage("");
          fetchPartnerData();
        }, 1500);
      } else {
        setStatusUpdateMessage(response.message || "Failed to update status");
      }
    } catch (error) {
      setStatusUpdateMessage("Error updating status");
    }
    setStatusUpdateLoading(false);
  };

  const handleShowCreatePartner = () => {
    setMode("create");
    setStepToShow(2);
  };
  const handleBackToTable = () => {
    setCanvasData(null);
    setStepToShow(1);
    setShowDetailsCanvas(false);
    setFormData(null);
  };

  useEffect(() => {
    let timeoutId;

    const fetchData = () => {
      fetchPartnerData(currentPage, searchText);
    };

    if (searchText) {
      timeoutId = setTimeout(fetchData, 500);
    } else {
      fetchData();
    }

    return () => clearTimeout(timeoutId);
  }, [currentPage, searchText]);


  const handleClickRow = async (data) => {
    if (data.code) {
      setCanvasData({});
      setShowDetailsCanvas(true);
      await handleViewDetails(data.code);
      setStepToShow(1);
    }
  };

  const handleClickUpdatePartner = async (data) => {
    if (data && data.code) {
      setMode("update");
      await handleUpdatePartner(data.code);
      setStepToShow(2);
    }
  };


  const handleNewPartnerAdded = () => {
    fetchPartnerData();
    handleBackToTable();
    setStepToShow(1);
  };

  return (
    <div className={styles.container} style={{ position: "relative" }}>
      {stepToShow === 1 && (
        <>
          <div className={styles.topControls}>
            <div className={styles.searchContainer}>
              <InputBox
                placeholder="Search by Partner name, Partner code..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {errorMessage && <div style={{ color: "red" }}>{errorMessage}</div>}
          {dataLoading ? (
            <Spinner />
          ) : (
            <>
              {Array.isArray(partners) && partners.length > 0 ? (
                <>
                  <DataTable
                    columns={partnerColumns(featureAccess)}
                    list={partners}
                    handleClickRow={handleClickRow}
                    handleViewDetails={handleViewDetails}
                    totalPages={totalPages}
                    currentPage={currentPage}
                    setCurrentPage={handlePageChange}
                    pagination={true}
                    title="Partners List"
                    titleButton={
                      writeAccess && (
                        <div style={{ display: "flex", gap: "10px" }}>
                          <Button
                            onClick={() => setShowBulkModal(true)}
                            title={"Bulk Upload"}
                            classes={styles.autoWidthButton}
                          />
                          <Button
                            onClick={handleShowCreatePartner}
                            title={"Create Partner"}
                            classes={styles.autoWidthButton}
                          />
                        </div>
                      )
                    }
                    handleUpdatePartner={handleClickUpdatePartner}
                    handleUpdateStatus={handleUpdateStatus}
                    handleAddConfig={handleAddConfig}
                  />
                </>
              ) : (
                <p
                  style={{
                    color: "white",
                    marginTop: "80px",
                    textAlign: "center",
                  }}
                >
                  No Data Found
                </p>
              )}
            </>
          )}
        </>
      )}

      {stepToShow === 2 && (
        <div className={styles.createPartnerContainer}>
          <div className={styles.backButtons}>
            <img
              alt="Back"
              src={singleBackArrow}
              onClick={handleBackToTable}
              className={styles.arrowButton}
            ></img>
            <span
              style={{
                fontSize: "13px",
                marginLeft: "5px",
                cursor: "pointer",
                color: "white",
              }}
              onClick={handleBackToTable}
            >
              Back
            </span>
          </div>
          <CreatePartner
            mode={mode}
            initialData={formData}
            successMessage={successMessage}
            setStepToShow={setStepToShow}
            newPartnerAdded={handleNewPartnerAdded}
          />
        </div>
      )}
      {stepToShow === 3 && (
        <div className={styles.createPartnerContainer}>
          <div className={styles.backButtons}>
            <img
              alt="Back"
              src={singleBackArrow}
              onClick={handleBackToTable}
              className={styles.arrowButton}
            />
            <span
              style={{
                fontSize: "13px",
                marginLeft: "5px",
                cursor: "pointer",
                color: "white",
              }}
              onClick={handleBackToTable}
            >
              Back
            </span>
          </div>
          <PartnerConfig
            partnerCd={selectedPartnerCd}
            onBack={handleBackToTable}
          />
        </div>
      )}


      <StatusUpdate
        show={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onSubmit={submitStatusUpdate}
        loading={statusUpdateLoading}
        statusMessage={statusUpdateMessage}
        partnerName={selectedPartner?.name}
        status={newStatus}
        setStatus={setNewStatus}
        showStatusDropdown={showStatusDropdown}
        setShowStatusDropdown={setShowStatusDropdown}
      />

      <BulkCreatePartner
        show={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onSuccess={() => {
          setShowBulkModal(false);
          fetchPartnerData();
        }}
        userName={getCurrentUsername()}
        existingPartners={partners}
      />



      <Offcanvas
        show={showDetailsCanvas}
        onHide={() => {
          setShowDetailsCanvas(false);
          setStepToShow(1);
        }}
        placement="end"
        className={styles.offcanvas}
        backdropClassName={styles.backgroundBlur}
        style={{ color: "white", backgroundColor: "#28282B", width: "650px" }}
      >
        <Offcanvas.Header
          closeButton
          style={{
            backgroundColor: "#343434",
            color: "white",
            fontSize: "20px",
            height: "50px",
          }}
        >
          <Offcanvas.Title
            style={{ color: "white", width: "100%", margin: "20px 20px" }}
          >
            Partner Details
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {dataLoading ? (
            <div
              style={{
                height: "100%",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Spinner animation="border" variant="light" />
            </div>
          ) : canvasData ? (
            <DetailsTable canvasData={canvasData} />
          ) : (
            <p style={{ color: "white", textAlign: "center" }}>
              No Partner Details Available
            </p>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
}

export default withFeatureActions(Partner, "INVESTING", "Partners");
